/**
 * Builds the room's walls from the structural boundary walk: every leaf a
 * square-ended slab (its inner line, one thickness across, run out past each
 * inner corner as far as the mitered outer corner reaches, and cut off
 * square), grouped by primary wall for the cutaway. Neighbors overlap inside
 * every corner instead of meeting on a diagonal: from outside the two outer
 * faces still meet at the corner, from inside the overlap is buried, and
 * wherever a neighbor is absent (an opening reaching the corner, or the
 * cutaway hiding that wall) the wall shows a butt end, as built.
 *
 * Each wall stands on the floor slab and stops under the ceiling slab: it
 * spans exactly [0, H], and the slabs carry the thickness out to the room's
 * outer perimeter (see `wallOuterRing`). Doors, windows, and openings are
 * punched straight through as cutouts (the wall material above and below an
 * opening belongs to the same leaf), and the door leaves and window glass
 * are added on top (see doorFixtures).
 *
 * One rule for every edge of a cutout: wherever the cutout touches an edge of
 * its wall leaf, no material and no reveal is drawn along that edge. Reaching
 * the start or end of the leaf (an edge within half an inch counts) means the
 * opening runs out through that square end and has no jamb there; a base of
 * 0 means no sill; a top of H means no lintel. A floor-to-ceiling opening is
 * a U open at both ends, one that also spans the whole leaf draws nothing at
 * all, and two openings meeting at a corner leave the corner empty.
 *
 * Outlines follow what is visible (see wallOutlines): a square end's edges
 * are drawn only where the end is exposed, over a neighbor's opening reaching
 * the junction and (as a surface culled with the neighbor's group) while the
 * cutaway hides that neighbor, so an ordinary corner draws exactly as a
 * mitered one did.
 */

import { EDGE_NAMES, type FloorPlanEdgeSegment, type FloorPlanRoom, type Point, WALL_THICKNESS } from '@kaiizen/kai-floor-plan';
import { PALETTE, segmentSelectionFor, Selection } from '../editor-model';
import { type PerimRef } from '../math2d';
import { dot3d, Vec3 } from '../math3d';
import { leafPoint, reachesEnd, reachesStart, slabCutoutSpan, slabRange, slabSolidSpans, type WallCutout, type WallLeaf, wallWalk } from '../room-geometry';
import { buildDoor, buildWindow, type WallOpening } from './doorFixtures';
import { MeshBuilder, type RGB, type SceneWallLeaf } from './mesh3d';
import { type Pickable3D, sameSelection } from './pick3d';
import { type PlanToWorld } from './sceneBox';
import {
  heightSpan,
  intersectSpans,
  outlineEnd,
  outlineHole,
  outlineRuns,
  reachesCeiling,
  reachesFloor,
  type SlabPoint,
  type Span,
  subtractSpans,
} from './wallOutlines';

// The thickness is the model's shared constant; re-exported for the view code and tests that read it here.
export { WALL_THICKNESS };

/** Where a selected door's or opening's selection pane stands: slightly room-side of the inner face, in slab thicknesses. */
const SELECTION_PANE_SIDE = -0.2;

/**
 * One end of a leaf's slab: the neighbor leaf's cull group, and the height
 * spans where that neighbor is open right at the junction (its cutouts
 * reaching it), where this leaf's square end shows even while the neighbor does.
 */
interface Joint {
  neighborGroup: number;
  neighborOpen: Span[];
}
interface LeafJoints {
  start: Joint;
  end: Joint;
}

/** What every leaf of the room's walls is built with. */
interface WallBuild {
  meshBuilder: MeshBuilder;
  shape: FloorPlanRoom;
  toWorld: PlanToWorld;
  height: number;
  color: RGB;
  pickables?: Pickable3D[];
  selection?: Selection;
}

/** One leaf being built: the walls' build plus the leaf, its cull group, its slab's world points, and its world unit normals. */
interface LeafBuild extends WallBuild {
  leaf: WallLeaf;
  group: number;
  slabPoint: SlabPoint;
  inwardNormal: Vec3;
  outwardNormal: Vec3;
  alongNormal: Vec3;
  backAlongNormal: Vec3;
}

/** One square end of a leaf's slab, for its outlines: the inner corner and the slab end along the leaf, the heights solid there, and its joint. */
interface LeafEnd {
  corner: number;
  slabEnd: number;
  solid: Span[];
  joint: Joint;
  solidAtFloor: boolean;
  solidAtCeiling: boolean;
}

/** The primary wall a leaf belongs to; a room-corner chamfer joins whichever primary wall it most faces. */
function groupOfLeaf(ref: PerimRef, outward: Point, wallInwardNormals: Vec3[]): number {
  if (ref.k === 'wall' || ref.k === 'seg' || ref.k === 'alcoveWall' || ref.k === 'alcoveCorner') {
    return EDGE_NAMES.indexOf(ref.edge);
  }
  const inward: Vec3 = [-outward.x, 0, -outward.y];
  let best = 0, bestFacing = -Infinity;
  wallInwardNormals.forEach((wallNormal, index) => {
    const facing = dot3d(inward, wallNormal);
    if (facing > bestFacing) {
      bestFacing = facing;
      best = index;
    }
  });
  return best;
}

export function buildWalls(
  meshBuilder: MeshBuilder,
  shape: FloorPlanRoom,
  toWorld: PlanToWorld,
  height: number,
  wallInwardNormals: Vec3[],
  wallColor: RGB,
  pickables?: Pickable3D[],
  selection?: Selection,
): void {
  const leaves = wallWalk(shape, height);
  if (leaves.length < 3) return;
  const groups = leaves.map(leaf => groupOfLeaf(leaf.ref, leaf.line.perp, wallInwardNormals));
  recordWalls(meshBuilder, leaves, groups, height);
  const walls: WallBuild = { meshBuilder, shape, toWorld, height, color: wallColor, pickables, selection };
  leaves.forEach((leaf, index) => buildLeaf(walls, leaf, groups[index], leafJoints(leaves, groups, index)));
}

/**
 * Records each primary wall's leaves for the exporters: rotates the walk to a
 * group boundary, then files each contiguous run of same-group leaves, with
 * their cutouts, as one wall. Exporters lower every leaf to its own
 * square-ended body with real holes.
 */
function recordWalls(meshBuilder: MeshBuilder, leaves: WallLeaf[], groups: number[], height: number): void {
  const count = leaves.length;
  const firstBoundary = Math.max(0, groups.findIndex((group, index) => group !== groups[(index - 1 + count) % count]));
  let runLeaves: SceneWallLeaf[] = [];
  let runGroup = -1;
  const flushWall = () => {
    if (runLeaves.length) meshBuilder.wall({ name: `${EDGE_NAMES[runGroup].replace(/Edge$/, '')} wall`, leaves: runLeaves, base: 0, top: height });
    runLeaves = [];
  };
  for (let step = 0; step < count; step++) {
    const index = (firstBoundary + step) % count;
    if (groups[index] !== runGroup) {
      flushWall();
      runGroup = groups[index];
    }
    runLeaves.push(sceneWallLeaf(leaves, index));
  }
  flushWall();
}

/** A leaf as the exporters record it: its inner line, the mitered outer line out to the next leaf's, and its cutouts. */
function sceneWallLeaf(leaves: WallLeaf[], index: number): SceneWallLeaf {
  const leaf = leaves[index];
  return {
    innerStart: leaf.innerStart,
    innerEnd: leaf.line.end,
    outerStart: leaf.outerStart,
    outerEnd: leaves[(index + 1) % leaves.length].outerStart,
    length: leaf.line.length,
    startExtension: leaf.startExtension,
    endExtension: leaf.endExtension,
    cutouts: leaf.cutouts.map(({ s0, s1, base, top }) => ({ s0, s1, base, top })),
  };
}

/** Where leaf `index` meets its neighbors: their cull groups, and where each is open right at the shared junction. */
function leafJoints(leaves: WallLeaf[], groups: number[], index: number): LeafJoints {
  const count = leaves.length;
  const previous = (index - 1 + count) % count, next = (index + 1) % count;
  const previousLeaf = leaves[previous];
  return {
    start: {
      neighborGroup: groups[previous],
      neighborOpen: previousLeaf.cutouts.filter(cutout => reachesEnd(cutout, previousLeaf.line.length)).map(heightSpan),
    },
    end: { neighborGroup: groups[next], neighborOpen: leaves[next].cutouts.filter(reachesStart).map(heightSpan) },
  };
}

/** The editor Selection a cutout stands for, from its leaf's PerimRef, in its canonical form (the one `select` stores). */
function selectionForCutout(shape: FloorPlanRoom, ref: PerimRef, segment: FloorPlanEdgeSegment): Selection | null {
  switch (ref.k) {
    case 'seg':
      return segmentSelectionFor(ref.edge, ref.index, segment);
    case 'alcoveWall': {
      const segmentIndex = shape[ref.edge]?.segments[ref.index]?.[ref.wall]?.segments.indexOf(segment) ?? -1;
      return segmentIndex >= 0 ? { kind: 'lineSeg', ref: { k: 'aw', edge: ref.edge, storageIndex: ref.index, wall: ref.wall }, si: segmentIndex } : null;
    }
    case 'alcoveCorner': {
      const segmentIndex = shape[ref.edge]?.segments[ref.index]?.[ref.which]?.segments?.indexOf(segment) ?? -1;
      return segmentIndex >= 0 ? { kind: 'lineSeg', ref: { k: 'ac', edge: ref.edge, storageIndex: ref.index, which: ref.which }, si: segmentIndex } : null;
    }
    case 'roomCorner': {
      const segmentIndex = shape[ref.corner]?.segments?.indexOf(segment) ?? -1;
      return segmentIndex >= 0 ? { kind: 'lineSeg', ref: { k: 'rc', corner: ref.corner }, si: segmentIndex } : null;
    }
    default:
      return null;
  }
}

/**
 * One leaf, on its wall's cull group: its slab, its cutouts with their
 * fixtures, the inner floor and ceiling lines, and its square ends' outlines
 * where a neighbor's opening exposes them; then, on a surface culled with
 * the neighbor wall, the whole end for while the cutaway hides that neighbor.
 */
function buildLeaf(walls: WallBuild, leaf: WallLeaf, group: number, joints: LeafJoints): void {
  const leafBuild = leafBuildFor(walls, leaf, group);
  const { meshBuilder, height, slabPoint } = leafBuild;
  const length = leaf.line.length;
  const fullHeight: Span[] = [[0, height]];
  const solidAtStart = subtractSpans(fullHeight, leaf.cutouts.filter(reachesStart).map(heightSpan));
  const solidAtEnd = subtractSpans(fullHeight, leaf.cutouts.filter(cutout => reachesEnd(cutout, length)).map(heightSpan));

  meshBuilder.begin(group);
  buildSlab(leafBuild, solidAtStart, solidAtEnd);
  for (const cutout of leaf.cutouts) buildCutout(leafBuild, cutout);
  outlineRuns(meshBuilder, slabPoint, length, height, leaf.cutouts);
  const ends = leafEnds(leafBuild, joints, solidAtStart, solidAtEnd);
  for (const end of ends) {
    outlineEnd(
      meshBuilder,
      slabPoint,
      end.corner,
      end.slabEnd,
      intersectSpans(end.solid, end.joint.neighborOpen),
      end.solidAtFloor,
      end.solidAtCeiling,
      height,
    );
  }
  meshBuilder.end();

  for (const end of ends) {
    if (end.joint.neighborGroup === group) continue;
    meshBuilder.begin(group, end.joint.neighborGroup);
    outlineEnd(meshBuilder, slabPoint, end.corner, end.slabEnd, end.solid, end.solidAtFloor, end.solidAtCeiling, height);
    meshBuilder.end();
  }
}

function leafBuildFor(walls: WallBuild, leaf: WallLeaf, group: number): LeafBuild {
  const { perp, dir } = leaf.line;
  return {
    ...walls,
    leaf,
    group,
    slabPoint: (s, side, height) => {
      const point = leafPoint(leaf, s, side);
      return walls.toWorld(point.x, point.y, height);
    },
    inwardNormal: [-perp.x, 0, -perp.y],
    outwardNormal: [perp.x, 0, perp.y],
    alongNormal: [dir.x, 0, dir.y],
    backAlongNormal: [-dir.x, 0, -dir.y],
  };
}

/**
 * The leaf's slab: its inner line run out through both square ends, one
 * thickness straight across (side 0 the inner face, 1 the outer), never
 * mitered. It is built from tiled, abutting faces, so no seam lines show
 * between them: runs between openings over the whole height, and only where
 * material actually remains, a sill below and a lintel above each opening
 * (the wall spans exactly [0, height], so a cutout touching the floor has no
 * sill and one touching the ceiling has no lintel). The top and bottom caps
 * cover the solid spans only, and the square ends stand wherever the leaf is
 * solid there. Inside a corner an end sits within the neighbor's slab (or
 * on its outer face, same color, same facing) and never shows; where the
 * neighbor is open at the junction, or hidden by the cutaway, it is the
 * wall's butt end.
 */
function buildSlab(leafBuild: LeafBuild, solidAtStart: Span[], solidAtEnd: Span[]): void {
  const { meshBuilder, leaf, height, color, slabPoint, inwardNormal, outwardNormal, alongNormal, backAlongNormal } = leafBuild;
  const [slabStart, slabEnd] = slabRange(leaf);
  const faces = (start: number, end: number, bottom: number, top: number) => {
    if (end - start < 1e-4 || top - bottom < 1e-4) return;
    meshBuilder.quad(slabPoint(start, 0, bottom), slabPoint(end, 0, bottom), slabPoint(end, 0, top), slabPoint(start, 0, top), inwardNormal, color);
    meshBuilder.quad(slabPoint(start, 1, bottom), slabPoint(end, 1, bottom), slabPoint(end, 1, top), slabPoint(start, 1, top), outwardNormal, color);
  };
  for (const [start, end] of slabSolidSpans(leaf)) faces(start, end, 0, height);
  for (const cutout of leaf.cutouts) {
    const [start, end] = slabCutoutSpan(leaf, cutout);
    faces(start, end, 0, cutout.base);
    faces(start, end, cutout.top, height);
  }
  for (const [start, end] of slabSolidSpans(leaf, cutout => reachesCeiling(cutout, height))) {
    meshBuilder.quad(slabPoint(start, 0, height), slabPoint(end, 0, height), slabPoint(end, 1, height), slabPoint(start, 1, height), [0, 1, 0], color);
  }
  for (const [start, end] of slabSolidSpans(leaf, reachesFloor)) {
    meshBuilder.quad(slabPoint(start, 0, 0), slabPoint(end, 0, 0), slabPoint(end, 1, 0), slabPoint(start, 1, 0), [0, -1, 0], color);
  }
  for (const [y0, y1] of solidAtStart) {
    meshBuilder.quad(
      slabPoint(slabStart, 0, y0),
      slabPoint(slabStart, 1, y0),
      slabPoint(slabStart, 1, y1),
      slabPoint(slabStart, 0, y1),
      backAlongNormal,
      color,
    );
  }
  for (const [y0, y1] of solidAtEnd) {
    meshBuilder.quad(slabPoint(slabEnd, 0, y0), slabPoint(slabEnd, 1, y0), slabPoint(slabEnd, 1, y1), slabPoint(slabEnd, 0, y1), alongNormal, color);
  }
}

/**
 * One cutout of the leaf: the reveals lining it, its outline, its door or
 * window, the selection pane of a selected door or opening, and its pick
 * target. Every reveal normal points into the opening: the jamb at its start
 * faces along the leaf, the jamb at its end back along it, the lintel's
 * underside down, the sill's top up. An edge the cutout touches (a leaf end,
 * the floor, the ceiling) has no reveal: there is no material there to line.
 */
function buildCutout(leafBuild: LeafBuild, cutout: WallCutout): void {
  const { meshBuilder, leaf, group, height, color, slabPoint, alongNormal, backAlongNormal, shape, pickables, selection } = leafBuild;
  const { base, top } = cutout;
  const length = leaf.line.length;
  const [start, end] = slabCutoutSpan(leaf, cutout);
  if (!reachesStart(cutout)) {
    meshBuilder.quad(slabPoint(start, 0, base), slabPoint(start, 1, base), slabPoint(start, 1, top), slabPoint(start, 0, top), alongNormal, color);
  }
  if (!reachesEnd(cutout, length)) {
    meshBuilder.quad(slabPoint(end, 0, base), slabPoint(end, 1, base), slabPoint(end, 1, top), slabPoint(end, 0, top), backAlongNormal, color);
  }
  if (!reachesCeiling(cutout, height)) {
    meshBuilder.quad(slabPoint(start, 0, top), slabPoint(end, 0, top), slabPoint(end, 1, top), slabPoint(start, 1, top), [0, -1, 0], color);
  }
  if (!reachesFloor(cutout)) {
    meshBuilder.quad(slabPoint(start, 0, base), slabPoint(end, 0, base), slabPoint(end, 1, base), slabPoint(start, 1, base), [0, 1, 0], color);
  }
  outlineHole(meshBuilder, slabPoint, cutout, start, end, length, height);
  const cutoutSelection = selectionForCutout(shape, leaf.ref, cutout.seg);
  const selected = !!cutoutSelection && !!selection && sameSelection(cutoutSelection, selection);
  if (cutout.kind === 'window') buildWindow(wallOpening(leafBuild, cutout, selected));
  else if (cutout.kind === 'door') buildCutoutDoor(leafBuild, cutout, selected, cutoutSelection);
  if (selected && cutout.kind !== 'window') drawSelectionPane(leafBuild, cutout);
  if (cutoutSelection && pickables) {
    pickables.push({
      sel: cutoutSelection,
      group,
      kind: 'rect',
      origin: slabPoint(cutout.s0, 0, base),
      along: alongNormal,
      sLen: cutout.s1 - cutout.s0,
      yLen: top - base,
    });
  }
}

/** The cutout as its door or window builder sees it: positions measured into the room from the leaf's inner face. */
function wallOpening({ meshBuilder, leaf, toWorld, alongNormal, inwardNormal }: LeafBuild, cutout: WallCutout, selected: boolean): WallOpening {
  const planPoint = (s: number, p: number): Point => leafPoint(leaf, s, -p / WALL_THICKNESS);
  return {
    meshBuilder,
    worldPoint: (s, p, height) => {
      const point = planPoint(s, p);
      return toWorld(point.x, point.y, height);
    },
    planPoint,
    start: cutout.s0,
    end: cutout.s1,
    base: cutout.base,
    top: cutout.top,
    alongNormal,
    inwardNormal,
    selected,
  };
}

/**
 * The cutout's door. Its box (the opening through the wall's thickness) tags
 * its own surface, so the assembler can drop this door's every panel when
 * another room already placed the same doorway (a shared door drawn from
 * both sides); the wall's cutout stays either way. Each panel is one solid
 * of the export tree and its own pick target, so tapping a panel standing
 * out in the room selects the door, not just the wall-opening rect below.
 */
function buildCutoutDoor(leafBuild: LeafBuild, cutout: WallCutout, selected: boolean, cutoutSelection: Selection | null): void {
  const { meshBuilder, leaf, group, pickables } = leafBuild;
  const door = meshBuilder.door({
    center: leafPoint(leaf, (cutout.s0 + cutout.s1) / 2, 0.5),
    along: { x: leaf.line.dir.x, y: leaf.line.dir.y },
    halfLength: (cutout.s1 - cutout.s0) / 2,
    halfWidth: WALL_THICKNESS / 2,
    base: cutout.base,
    top: cutout.top,
  });
  meshBuilder.begin(group, undefined, door);
  const panelTargets = cutoutSelection && pickables ? { selection: cutoutSelection, group, pickables } : null;
  buildDoor(wallOpening(leafBuild, cutout, selected), cutout.swing ?? 'inLeft', door, panelTargets);
  meshBuilder.begin(group);
}

/** A selected door's or bare opening's selection pane: the opening has no wall mesh to tint, so a translucent accent pane marks it (a window tints its glass instead). */
function drawSelectionPane({ meshBuilder, slabPoint, inwardNormal }: LeafBuild, cutout: WallCutout): void {
  const corners = [
    slabPoint(cutout.s0, SELECTION_PANE_SIDE, cutout.base),
    slabPoint(cutout.s1, SELECTION_PANE_SIDE, cutout.base),
    slabPoint(cutout.s1, SELECTION_PANE_SIDE, cutout.top),
    slabPoint(cutout.s0, SELECTION_PANE_SIDE, cutout.top),
  ];
  meshBuilder.glassQuad(corners[0], corners[1], corners[2], corners[3], inwardNormal, PALETTE.accent);
  meshBuilder.polyline(corners, true);
}

/** The leaf's two square ends, for their outlines: an end is solid through the floor or ceiling unless a cutout reaching that end also reaches it. */
function leafEnds({ leaf, height }: LeafBuild, joints: LeafJoints, solidAtStart: Span[], solidAtEnd: Span[]): LeafEnd[] {
  const length = leaf.line.length;
  const [slabStart, slabEnd] = slabRange(leaf);
  const solidThrough = (atStart: boolean, reaches: (cutout: WallCutout) => boolean) =>
    !leaf.cutouts.some(cutout => (atStart ? reachesStart(cutout) : reachesEnd(cutout, length)) && reaches(cutout));
  return [
    {
      corner: 0,
      slabEnd: slabStart,
      solid: solidAtStart,
      joint: joints.start,
      solidAtFloor: solidThrough(true, reachesFloor),
      solidAtCeiling: solidThrough(true, cutout => reachesCeiling(cutout, height)),
    },
    {
      corner: length,
      slabEnd,
      solid: solidAtEnd,
      joint: joints.end,
      solidAtFloor: solidThrough(false, reachesFloor),
      solidAtCeiling: solidThrough(false, cutout => reachesCeiling(cutout, height)),
    },
  ];
}
